import { useState } from 'react';
import {Link, useNavigate} from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { authService } from '@/services/auth-service.js';
import StarrySkyBackground from "@/components/StarrySkyBackground";
import fantasyForest from "@/assets/fantasy_forest_big.jpg";
import googleSvg from "@/assets/svg/google_g.svg";


const SignupPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [emailError, setEmailError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [marketingConsent, setMarketingConsent] = useState(false);
    const navigate = useNavigate();

    const validatePassword = (pass) => {
        return pass.length >= 8;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setEmailError('');
        setPasswordError('');

        if (!validatePassword(password)) {
            setPasswordError('Password must be at least 8 characters long.');
            return;
        }

        try {
            await authService.register(email, password, marketingConsent);
            navigate('/home');
        } catch (error) {
            // Check for the specific error format from the API
            // @ts-expect-error ignoring
            if (error.response?.status === 400 && error.response?.data?.error === 'Email already in use') {
                setEmailError('This email is already in use.');
            } else {
                // Handle other types of errors
                setPasswordError('Registration failed. Please try again.');
                console.error('Registration error:', error);
            }
        }
    };

    return (
        <div className="grid flex-col w-screen h-full place-items-center lg:flex-row">
            <div className="flex flex-col w-screen min-h-full lg:flex-row">
                <div className="relative hidden lg:flex lg:w-1/2">
                    <div className="flex items-center justify-center w-full h-full border-r-2 bg-input dark:bg-input dark:border-accent-foreground">
                        <img
                            src={fantasyForest}
                            alt="Sign In Illustration"
                            className="absolute object-fill w-full h-full"
                        />
                    </div>
                </div>

                <div className="flex items-center justify-center w-full p-6 lg:w-1/2">
                    <StarrySkyBackground showShootingStar={false} showAurora={false} showClouds={false} />
                    <div className="w-full max-w-md space-y-6">
                        <div className="space-y-2 text-center">
                            <h2 className="text-2xl font-bold">Create Your Account</h2>
                            <p className="dark:text-neutral-50 hover:text-gray-900">
                                Already have an account?{' '}
                                <Link to="/signIn" className="text-white dark:text-primary hover:underline">
                                    Sign In
                                </Link>
                            </p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="Enter your email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                                {emailError && <p className="mt-1 text-sm text-red-500">{emailError}</p>}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="password">Password</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    placeholder="Create a password (min 8 characters)"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                />
                                {passwordError && <p className="mt-1 text-sm text-red-500">{passwordError}</p>}
                            </div>

                            <Button type="submit" className="w-full bg-sky-100 hover:bg-sky-300"
                            >
                                Continue
                            </Button>

                            <div className="flex items-center space-x-2">
                                <Checkbox
                                    id="marketing"
                                    checked={marketingConsent}
                                    onCheckedChange={(checked) => setMarketingConsent(checked === true)}
                                />
                                <Label
                                    htmlFor="marketing"
                                    className="text-sm"
                                >
                                    Keep me updated with news and special offers
                                </Label>
                            </div>
                        </form>

                        <div className="flex items-center w-5/12 my-4 ml-1 lg:ml-3">
                            <Separator className="flex-grow bg-slate-900 dark:bg-slate-100" />
                            <span className="px-4">or</span>
                            <Separator className="flex-grow bg-slate-900 dark:bg-slate-100" />
                        </div>

                        <Button
                            variant="outline"
                            className="relative w-full bg-white dark:bg-black hover:bg-sky-300"
                            onClick={authService.handleGoogleAuth}
                        >
                            <img
                                src={googleSvg}
                                alt="Google Icon"
                                className="w-5 h-5 mr-2"
                            />
                            Continue with Google
                        </Button>

                        <p className="mt-4 text-xs text-center text-foreground">
                            By continuing, you agree to our{' '}
                            <Link to="/terms" className="underline">Terms of Service</Link>{' '}
                            and{' '}
                            <Link to="/privacy" className="underline">Privacy Policy</Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SignupPage;
